import React from "react";
import Typography from "@material-ui/core/Typography";
import { Link as RouterLink } from "react-router-dom";

import { APP_STRINGS } from "constants/appStrings";
import { clientPortalMock } from "mocks/clientPortalMock";

import "./index.css";

const badgeClassMap = {
  pendente: "client-portal__status client-portal__status--pending",
  urgente: "client-portal__status client-portal__status--urgent",
  "em analise": "client-portal__status client-portal__status--review",
};

const timelineTypeMap = {
  update: "Atualizacao",
  document: "Documento",
  meeting: "Reuniao",
  onboarding: "Onboarding",
};

export default function ClientPortalPage() {
  const { client, caseSummary, highlights, nextSteps, timeline, documents, appointments, messages, financial } =
    clientPortalMock;

  return (
    <div className="client-portal">
      <div className="client-portal__background" />
      <div className="client-portal__glow client-portal__glow--top" />
      <div className="client-portal__glow client-portal__glow--bottom" />

      <main className="client-portal__shell">
        <header className="client-portal__topbar">
          <div>
            <span className="client-portal__brand">{APP_STRINGS.brand.desktop}</span>
            <Typography component="h1" className="client-portal__title">
              Portal da cliente
            </Typography>
          </div>

          <div className="client-portal__top-actions">
            <RouterLink className="client-portal__top-link" to="/login">
              Voltar ao login
            </RouterLink>
            <RouterLink className="client-portal__top-button" to="/">
              Site institucional
            </RouterLink>
          </div>
        </header>

        <section className="client-portal__hero">
          <div className="client-portal__hero-main">
            <span className="client-portal__eyebrow">Area do cliente em demonstracao</span>
            <Typography component="h2" className="client-portal__hero-title">
              Acompanhamento claro, elegante e organizado para cada etapa do atendimento.
            </Typography>
            <Typography component="p" className="client-portal__hero-text">
              Esta tela simula a experiencia de um portal premium para clientes do
              escritorio, reunindo status, documentos, comunicacoes, agenda e
              proximos passos em um unico ambiente.
            </Typography>

            <div className="client-portal__summary-card">
              <div className="client-portal__summary-head">
                <div>
                  <span className="client-portal__summary-tag">{caseSummary.area}</span>
                  <strong className="client-portal__summary-title">
                    {caseSummary.title}
                  </strong>
                </div>
                <span className="client-portal__summary-status">{caseSummary.status}</span>
              </div>

              <div className="client-portal__summary-meta">
                <span>Protocolo {caseSummary.protocol}</span>
                <span>{caseSummary.owner}</span>
                <span>{caseSummary.updatedAt}</span>
              </div>

              <p className="client-portal__summary-text">{caseSummary.summary}</p>
            </div>
          </div>

          <aside className="client-portal__profile-card">
            <span className="client-portal__profile-label">{client.accountStatus}</span>
            <strong className="client-portal__profile-name">{client.name}</strong>
            <span className="client-portal__profile-role">{client.role}</span>

            <div className="client-portal__profile-list">
              <div>
                <small>Perfil</small>
                <span>{client.profileType}</span>
              </div>
              <div>
                <small>E-mail</small>
                <span>{client.email}</span>
              </div>
              <div>
                <small>Telefone</small>
                <span>{client.phone}</span>
              </div>
              <div>
                <small>Cidade</small>
                <span>{client.city}</span>
              </div>
            </div>

            <div className="client-portal__profile-note">{client.since}</div>
          </aside>
        </section>

        <section className="client-portal__highlights">
          {highlights.map((item) => (
            <article key={item.label} className="client-portal__highlight-card">
              <span className="client-portal__highlight-label">{item.label}</span>
              <strong className="client-portal__highlight-value">{item.value}</strong>
              <p className="client-portal__highlight-text">{item.detail}</p>
            </article>
          ))}
        </section>

        <section className="client-portal__grid">
          <div className="client-portal__column client-portal__column--main">
            <article className="client-portal__panel">
              <div className="client-portal__panel-head">
                <div>
                  <span className="client-portal__panel-tag">Proximos passos</span>
                  <Typography component="h3" className="client-portal__panel-title">
                    Pendencias e acoes priorizadas
                  </Typography>
                </div>
                <span className="client-portal__panel-meta">{caseSummary.priority}</span>
              </div>

              <div className="client-portal__checklist">
                {nextSteps.map((item) => (
                  <div key={item.title} className="client-portal__check-item">
                    <div className="client-portal__check-main">
                      <strong>{item.title}</strong>
                      <span className={badgeClassMap[item.status]}>{item.status}</span>
                    </div>
                    <p>{item.description}</p>
                    <small>Prazo sugerido: {item.dueDate}</small>
                  </div>
                ))}
              </div>
            </article>

            <article className="client-portal__panel">
              <div className="client-portal__panel-head">
                <div>
                  <span className="client-portal__panel-tag">Timeline</span>
                  <Typography component="h3" className="client-portal__panel-title">
                    Historico do atendimento
                  </Typography>
                </div>
              </div>

              <div className="client-portal__timeline">
                {timeline.map((item) => (
                  <div key={`${item.date}-${item.title}`} className="client-portal__timeline-item">
                    <div className="client-portal__timeline-marker" />
                    <div className="client-portal__timeline-body">
                      <div className="client-portal__timeline-meta">
                        <span>{item.date}</span>
                        <span>{timelineTypeMap[item.type]}</span>
                      </div>
                      <strong>{item.title}</strong>
                      <p>{item.text}</p>
                    </div>
                  </div>
                ))}
              </div>
            </article>

            <article className="client-portal__panel">
              <div className="client-portal__panel-head">
                <div>
                  <span className="client-portal__panel-tag">Mensagens</span>
                  <Typography component="h3" className="client-portal__panel-title">
                    Comunicacoes recentes
                  </Typography>
                </div>
              </div>

              <div className="client-portal__messages">
                {messages.map((message) => (
                  <div key={`${message.time}-${message.subject}`} className="client-portal__message-card">
                    <div className="client-portal__message-head">
                      <strong>{message.subject}</strong>
                      <span>{message.time}</span>
                    </div>
                    <div className="client-portal__message-author">{message.author}</div>
                    <p>{message.body}</p>
                  </div>
                ))}
              </div>
            </article>
          </div>

          <div className="client-portal__column client-portal__column--side">
            <article className="client-portal__panel">
              <div className="client-portal__panel-head">
                <div>
                  <span className="client-portal__panel-tag">Documentos</span>
                  <Typography component="h3" className="client-portal__panel-title">
                    Pasta digital do caso
                  </Typography>
                </div>
              </div>

              <div className="client-portal__documents">
                {documents.map((item) => (
                  <div key={item.name} className="client-portal__document-card">
                    <strong>{item.name}</strong>
                    <span>{item.category}</span>
                    <div className="client-portal__document-meta">
                      <small>{item.status}</small>
                      <small>{item.updatedAt}</small>
                    </div>
                  </div>
                ))}
              </div>
            </article>

            <article className="client-portal__panel">
              <div className="client-portal__panel-head">
                <div>
                  <span className="client-portal__panel-tag">Agenda</span>
                  <Typography component="h3" className="client-portal__panel-title">
                    Reunioes e atendimentos
                  </Typography>
                </div>
              </div>

              <div className="client-portal__appointments">
                {appointments.map((item) => (
                  <div key={`${item.date}-${item.time}`} className="client-portal__appointment-card">
                    <strong>{item.type}</strong>
                    <span>
                      {item.date} as {item.time}
                    </span>
                    <small>{item.with}</small>
                    <p>{item.note}</p>
                  </div>
                ))}
              </div>
            </article>

            <article className="client-portal__panel">
              <div className="client-portal__panel-head">
                <div>
                  <span className="client-portal__panel-tag">Financeiro</span>
                  <Typography component="h3" className="client-portal__panel-title">
                    Honorarios e parcelas
                  </Typography>
                </div>
              </div>

              <div className="client-portal__finance-summary">
                <div>
                  <small>Total contratado</small>
                  <strong>{financial.total}</strong>
                </div>
                <div>
                  <small>Pago</small>
                  <strong>{financial.paid}</strong>
                </div>
                <div>
                  <small>Pendente</small>
                  <strong>{financial.pending}</strong>
                </div>
              </div>

              <div className="client-portal__finance-note">
                {financial.plan} • Proxima cobranca em {financial.nextCharge}
              </div>

              <div className="client-portal__invoice-list">
                {financial.invoices.map((item) => (
                  <div key={item.label} className="client-portal__invoice-item">
                    <div>
                      <strong>{item.label}</strong>
                      <span>{item.status}</span>
                    </div>
                    <strong>{item.amount}</strong>
                  </div>
                ))}
              </div>
            </article>
          </div>
        </section>
      </main>
    </div>
  );
}
